import { Modal, View, Text, Pressable } from 'react-native';
import { footballerQuizResultOverlayStyles as styles } from './FootballerQuizResultOverlay.styles';
import { FootballerFlag } from '../FootballerQuizView/FootballerFlag';
import { FOOTBALLER_TEST_IDS as IDS } from '../../../src/quiz/constants/footballerQuiz.constants';
import type { FootballerResult } from '../../../src/quiz/hooks/useFootballerQuizGame';
import type { FootballerGuessRow } from '../../../src/quiz/helpers/footballerQuizApi';

interface FootballerQuizResultOverlayProps {
  visible: boolean;
  result: FootballerResult | null;
  guesses: FootballerGuessRow[];
  maxGuesses: number;
  onShare: () => void;
  onLeaderboard: () => void;
  onNext: () => void;
}

function initials(name: string) {
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

/** Emoji grid line per guess (green tiles only — mock parity, mirrors the server share). */
function shareLine(row: FootballerGuessRow): string {
  return [row.nat.mark, row.league.mark, row.team.mark, row.pos.mark, row.age.mark]
    .map((m) => (m === 'green' ? '🟩' : '⬛'))
    .join('');
}

export function FootballerQuizResultOverlay({
  visible,
  result,
  guesses,
  maxGuesses,
  onShare,
  onLeaderboard,
  onNext,
}: FootballerQuizResultOverlayProps) {
  if (!result) return null;
  const { won, reveal, guessesUsed, streak, coinsAwarded } = result;
  const guessesLabel = won ? `${guessesUsed}/${maxGuesses}` : `X/${maxGuesses}`;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onNext}>
      <View style={styles.backdrop}>
        <View style={styles.sheet} testID={IDS.resultOverlay}>
          <View style={styles.handle} />

          <View style={styles.heading}>
            <Text style={styles.headingEmoji}>{won ? '🎉' : '😔'}</Text>
            <Text style={styles.headingTitle}>{won ? 'You called it!' : 'Better luck tomorrow'}</Text>
            <Text style={styles.headingSub}>{won ? `Guessed in ${guessesUsed}/${maxGuesses} tries` : 'The answer was...'}</Text>
          </View>

          {/* Reveal */}
          <View style={styles.revealCard}>
            <View style={styles.revealAvatar}>
              <Text style={styles.revealInitials}>{initials(reveal.name)}</Text>
            </View>
            <View style={styles.revealBody}>
              <Text style={styles.revealName} numberOfLines={1}>{reveal.name}</Text>
              <View style={styles.revealMetaRow}>
                <FootballerFlag country={reveal.nationality} width={18} height={13} />
                <Text style={styles.revealNat}>{reveal.nationality}</Text>
                <Text style={styles.revealDot}>·</Text>
                {reveal.club ? <Text style={styles.revealClub}>{reveal.club}</Text> : null}
                {reveal.position ? <Text style={styles.revealPos}>{reveal.position}</Text> : null}
              </View>
              <View style={styles.revealStatsRow}>
                <Text style={styles.revealStat}>Age <Text style={styles.revealStatStrong}>{reveal.age ?? '—'}</Text></Text>
                {reveal.leagueShort ? <Text style={styles.revealStat}>League <Text style={styles.revealStatStrong}>{reveal.leagueShort}</Text></Text> : null}
              </View>
            </View>
          </View>

          {/* Share grid */}
          <View style={styles.shareBox}>
            <Text style={styles.shareLabel}>YOUR RESULT</Text>
            {guesses.map((row, i) => (
              <Text key={`${row.playerId}-${i}`} style={styles.shareLine}>{shareLine(row)}</Text>
            ))}
          </View>

          {coinsAwarded > 0 ? (
            <View style={styles.coinsPill}>
              <Text style={styles.coinsText}>+{coinsAwarded} coins</Text>
            </View>
          ) : null}

          {/* Stats */}
          <View style={styles.statsRow}>
            <View style={styles.stat}>
              <Text style={styles.statNumStreak}>{streak}</Text>
              <Text style={styles.statLabel}>Streak 🔥</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.stat}>
              <Text style={styles.statNumGuesses}>{guessesLabel}</Text>
              <Text style={styles.statLabel}>Guesses</Text>
            </View>
          </View>

          {/* Actions */}
          <Pressable
            testID={IDS.shareButton}
            onPress={onShare}
            style={({ pressed }) => [styles.primaryButton, pressed && styles.pressed]}
          >
            <Text style={styles.primaryText}>Share result</Text>
          </Pressable>
          <View style={styles.secondaryRow}>
            <Pressable
              testID={IDS.leaderboardButton}
              onPress={onLeaderboard}
              style={({ pressed }) => [styles.secondaryButton, pressed && styles.pressed]}
            >
              <Text style={styles.secondaryText}>🏆 Leaderboard</Text>
            </Pressable>
            <Pressable
              testID={IDS.nextButton}
              onPress={onNext}
              style={({ pressed }) => [styles.secondaryButton, pressed && styles.pressed]}
            >
              <Text style={[styles.secondaryText, styles.secondaryTextDim]}>Next puzzle</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}
